/**
 * Inspection-pack question editor.
 * Same input shape as the SS checklist editor (title, Pass/Fail or number, unit, criteria).
 * Does not call SS checklist APIs.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, List, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import { parseChecklistImportText, readFileAsText } from '../utils/parseChecklistImport';

const TABS = [
  { id: 'overview', label: 'Overview', icon: List },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'add', label: 'Add question', icon: Plus },
];

const emptyItem = () => ({
  question: '',
  instruction: '',
  inspection_criteria: '',
  category: '',
  method_tool: '',
  unit: '',
  value_type: 'BOOL',
  pass_threshold: '',
});

function asEditorItem(item, index) {
  const valueType =
    item.value_type === 'NUM' || item.value_type === 'TEXT'
      ? item.value_type
      : item.kind === 'measure'
        ? 'NUM'
        : 'BOOL';
  return {
    id: item.id || `q-${index + 1}`,
    question: item.question || item.title || '',
    instruction: item.instruction || '',
    inspection_criteria: item.inspection_criteria || '',
    category: item.category || '',
    method_tool: item.method_tool || '',
    unit: item.unit || '',
    value_type: valueType,
    pass_threshold: item.pass_threshold || '',
    measure_key: item.measure_key,
  };
}

function typeLabel(vt) {
  if (vt === 'NUM') return 'Number';
  if (vt === 'TEXT') return 'Text';
  return 'Pass/Fail';
}

export default function InspectionQuestionEditor({ pack, onSave, onDelete, saving = false, embedded = false }) {
  const [tab, setTab] = useState('overview');
  const [items, setItems] = useState(() => (pack.items || []).map(asEditorItem));
  const [draft, setDraft] = useState(emptyItem);
  const [addMode, setAddMode] = useState('manual');
  const [importRows, setImportRows] = useState(null);
  const [importSelected, setImportSelected] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);

  function updateItem(index, patch) {
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function move(index, dir) {
    setItems((rows) => {
      const next = [...rows];
      const j = index + dir;
      if (j < 0 || j >= next.length) return rows;
      const [row] = next.splice(index, 1);
      next.splice(j, 0, row);
      return next;
    });
  }

  function addDraft() {
    if (!draft.question.trim()) return;
    setItems((rows) => [...rows, { ...draft, id: `q-${Date.now()}` }]);
    setDraft(emptyItem());
    setTab('edit');
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await readFileAsText(file);
    const parsed = parseChecklistImportText(text, file.name);
    setImportWarnings(parsed.warnings || []);
    setImportRows(parsed.items || []);
    setImportSelected((parsed.items || []).map((_, i) => i));
  }

  function addImported() {
    const picked = (importRows || []).filter((_, i) => importSelected.includes(i));
    if (!picked.length) return;
    setItems((rows) => [...rows, ...picked.map((row, i) => asEditorItem(row, rows.length + i))]);
    setImportRows(null);
    setImportSelected([]);
    setTab('edit');
  }

  return (
    <div className={embedded ? 'overflow-hidden rounded-xl border border-navy-200' : 'rounded-xl border bg-white overflow-hidden'}>
      <div className={embedded ? 'space-y-1 border-b border-navy-200 px-4 py-3' : 'px-4 py-3 border-b bg-slate-50 space-y-1'}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-sm">
            {pack.code} · {pack.name}
          </p>
          <button
            type="button"
            className="text-sm rounded bg-primary-600 text-white px-3 py-1.5 disabled:opacity-50"
            disabled={saving}
            onClick={() => onSave(pack.code, { ...pack, items })}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onDelete && (
            <button type="button" className="text-xs text-red-600 px-2" onClick={() => onDelete(pack.code)}>
              Hide pack
            </button>
          )}
        </div>
        {pack.description && <p className="text-xs text-gray-500">{pack.description}</p>}
      </div>
      <div className="flex flex-wrap gap-1 px-3 pt-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold border ${
              tab === t.id ? 'bg-primary-600 text-white border-primary-600' : embedded ? 'border-navy-200 text-gray-700' : 'bg-white text-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <ul className="divide-y mt-3">
          {!items.length && <li className="px-4 py-6 text-sm text-gray-500">No questions yet. Use Add question.</li>}
          {items.map((item, i) => (
            <li key={item.id || i} className="px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">
                {i + 1}. {item.question || 'Untitled'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {typeLabel(item.value_type)}
                {item.unit ? ` · ${item.unit}` : ''}
                {item.value_type === 'NUM' && item.pass_threshold ? ` · ${item.pass_threshold}` : ''}
              </p>
              {item.instruction && <p className="text-xs text-gray-600 mt-1">{item.instruction}</p>}
            </li>
          ))}
        </ul>
      )}

      {tab === 'edit' && (
        <ul className="divide-y mt-3">
          {!items.length && (
            <li className="px-4 py-6 text-sm text-gray-500">No questions yet. Use Add question.</li>
          )}
          {items.map((item, i) => (
            <li key={item.id || i} className="p-3 flex items-start gap-2">
              <div className="flex flex-col shrink-0 pt-1">
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 text-gray-500 disabled:opacity-30">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  disabled={i === items.length - 1}
                  onClick={() => move(i, 1)}
                  className="p-0.5 text-gray-500 disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-400 w-6">{i + 1}</span>
                  <input
                    className="flex-1 min-w-[10rem] border border-gray-200 rounded px-2 py-1.5 text-sm font-semibold"
                    value={item.question}
                    onChange={(e) => updateItem(i, { question: e.target.value })}
                    placeholder="Inspection item title"
                  />
                  <select
                    className="border border-gray-200 rounded px-2 py-1.5 text-xs"
                    value={item.value_type || 'BOOL'}
                    onChange={(e) => updateItem(i, { value_type: e.target.value })}
                  >
                    <option value="BOOL">Pass/Fail</option>
                    <option value="NUM">Number (auto P/F)</option>
                    <option value="TEXT">Text notes</option>
                  </select>
                </div>
                <textarea
                  rows={2}
                  className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm"
                  value={item.instruction || ''}
                  onChange={(e) => updateItem(i, { instruction: e.target.value })}
                  placeholder="Info — on-site how-to"
                />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input
                    className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                    value={item.category || ''}
                    onChange={(e) => updateItem(i, { category: e.target.value })}
                    placeholder="Category"
                  />
                  <input
                    className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                    value={item.method_tool || ''}
                    onChange={(e) => updateItem(i, { method_tool: e.target.value })}
                    placeholder="Method / tool"
                  />
                  <input
                    className="border border-gray-200 rounded px-2 py-1.5 text-sm"
                    value={item.inspection_criteria || ''}
                    onChange={(e) => updateItem(i, { inspection_criteria: e.target.value })}
                    placeholder="Pass criteria / standard"
                  />
                  <div className="flex gap-1">
                    <input
                      className="w-20 border border-gray-200 rounded px-2 py-1.5 text-sm"
                      value={item.unit || ''}
                      onChange={(e) => updateItem(i, { unit: e.target.value })}
                      placeholder="Unit"
                    />
                    <input
                      className="flex-1 border border-blue-200 rounded px-2 py-1.5 text-sm disabled:opacity-40"
                      value={item.pass_threshold || ''}
                      disabled={item.value_type !== 'NUM'}
                      onChange={(e) => updateItem(i, { pass_threshold: e.target.value })}
                      placeholder="e.g. <=67"
                    />
                  </div>
                </div>
              </div>
              <button type="button" className="p-1.5 text-red-600 hover:bg-red-50 rounded" onClick={() => setItems((rows) => rows.filter((_, j) => j !== i))}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {tab === 'add' && (
        <div className="p-4 space-y-4 max-w-xl">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-semibold max-w-md">
            <button
              type="button"
              onClick={() => setAddMode('manual')}
              className={`flex-1 py-2.5 ${addMode === 'manual' ? 'bg-primary-600 text-white' : embedded ? 'text-gray-700' : 'bg-white text-gray-700'}`}
            >
              Manual input
            </button>
            <button
              type="button"
              onClick={() => setAddMode('file')}
              className={`flex-1 py-2.5 border-l border-gray-200 ${addMode === 'file' ? 'bg-primary-600 text-white' : embedded ? 'text-gray-700' : 'bg-white text-gray-700'}`}
            >
              File upload
            </button>
          </div>
          {addMode === 'manual' ? (
            <div className="space-y-3">
              <button
                type="button"
                disabled={!draft.question.trim()}
                onClick={addDraft}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save
              </button>
              <p className="text-xs text-gray-500">Adds the question to this pack. Use Save on the pack header to store it.</p>
              <label className="block text-sm">
                Inspection item title
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={draft.question}
                  onChange={(e) => setDraft((p) => ({ ...p, question: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                Info (on-site how-to)
                <textarea
                  rows={3}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  value={draft.instruction}
                  onChange={(e) => setDraft((p) => ({ ...p, instruction: e.target.value }))}
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-sm">
                  Answer type
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    value={draft.value_type}
                    onChange={(e) => setDraft((p) => ({ ...p, value_type: e.target.value }))}
                  >
                    <option value="BOOL">Pass / Fail</option>
                    <option value="NUM">Number (auto Pass/Fail)</option>
                    <option value="TEXT">Text notes</option>
                  </select>
                </label>
                <label className="block text-sm">
                  Pass threshold
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:opacity-40"
                    value={draft.pass_threshold}
                    disabled={draft.value_type !== 'NUM'}
                    placeholder="e.g. <=67"
                    onChange={(e) => setDraft((p) => ({ ...p, pass_threshold: e.target.value }))}
                  />
                </label>
              </div>
              {[
                ['category', 'Category'],
                ['method_tool', 'Method / tool'],
                ['inspection_criteria', 'Pass criteria / standard'],
                ['unit', 'Unit'],
              ].map(([key, label]) => (
                <label key={key} className="block text-sm">
                  {label}
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={draft[key]}
                    onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Upload a CSV or JSON checklist. Tick rows, then add them to this pack.</p>
              <input type="file" accept=".csv,.json,.txt,text/csv,application/json" onChange={onImportFile} className="block w-full text-sm" />
              {importWarnings.length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 list-disc pl-5">
                  {importWarnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              {importRows && (
                <div className="space-y-2">
                  <button type="button" className="text-sm rounded bg-primary-600 text-white px-3 py-1.5" onClick={addImported}>
                    Add selected
                  </button>
                  {importRows.map((row, i) => (
                    <label key={row.id || i} className="flex gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={importSelected.includes(i)}
                        onChange={(e) =>
                          setImportSelected((sel) =>
                            e.target.checked ? [...sel, i] : sel.filter((n) => n !== i)
                          )
                        }
                      />
                      <span>{row.question}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
